import { createTheme } from "@mui/material/styles";
import {
  primary,
  primaryDark,
  primaryLight,
  secondary,
  white,
} from "./color.constants";

const keyboardFocusStyle = {
  "&:focus-visible": {
    outline: "5px auto -webkit-focus-ring-color",
    outlineOffset: "-2px",
  },
};

const getTextColor = (darkMode) =>
  darkMode ? undefined : "rgba(0, 0, 0, 0.85)";

function getTheme(darkMode) {
  return createTheme({
    palette: {
      mode: darkMode ? "dark" : "light",
      primary: {
        light: primary,
        dark: primaryDark,
        main: primary,
      },
      secondary: {
        main: secondary,
      },
      error: {
        main: darkMode ? "#FF5F5C" : "#a94442",
      },
    },
    components: {
      MuiAlert: {
        styleOverrides: {
          root: {
            "&.MuiAlert-standardInfo": {
              color: darkMode ? "#254250" : undefined,
              background: darkMode ? "#b2c3ca" : undefined,
            },
            "&.MuiAlert-standardError": {
              color: "#ea7371",
            },
          },
        },
      },
      MuiSvgIcon: {
        styleOverrides: {
          root: keyboardFocusStyle,
        },
      },
      MuiButtonBase: {
        styleOverrides: {
          root: keyboardFocusStyle,
        },
      },
      MuiChip: {
        styleOverrides: {
          root: keyboardFocusStyle,
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: "rgba(52, 60, 62, 1)",
            color: "white",
            backgroundImage: "none",
          },
          paperAnchorDockedLeft: {
            top: "auto",
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableFocusRipple: true,
        },
        styleOverrides: {
          contained: {
            background: darkMode ? primaryDark : primary,
            color: white,
            margin: "0 0.3125rem",
            padding: "0.4375rem 0.9375rem",
            "&:hover": {
              backgroundColor: darkMode
                ? "rgb(92 175 97 / 92%)"
                : "rgb(61 165 67 / 92%)",
            },
          },
          text: { color: darkMode ? primaryLight : undefined },
          outlined: { color: darkMode ? primaryLight : undefined },
          root: {
            ...keyboardFocusStyle,
            "&.customLink": {
              color: darkMode ? "#71cbf5" : "blue",
              textDecoration: "none",
              display: "inline-block",
              textTransform: "none",
              paddingLeft: 0,
              margin: "-0.5rem 0",
              fontWeight: "400",
              "&:hover, &:focus-visible": {
                textDecoration: "underline",
                backgroundColor: "transparent",
              },
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontSize: "1em",
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            "& strong": {
              fontSize: "1.1rem",
              fontWeight: darkMode ? 500 : 600,
              textShadow: "none",
            },
            "& i": {
              color: darkMode ? "#fff !important" : "grey",
            },
            "& small>b": {
              color: darkMode ? "#fff !important" : "#000",
            },
            "& .customLink": {
              color: darkMode ? "#71cbf5" : "blue",
              textDecoration: "none",
              "&:hover, &:focus-visible": { textDecoration: "underline" },
            },
          },
          h2: {
            fontSize: "1.6rem",
            fontWeight: darkMode ? 500 : 600,
          },
        },
      },
      MuiGrid: {
        styleOverrides: {
          root: {
            "& p>a, li>a, .customLink": {
              color: darkMode ? "#71cbf5" : "blue",
              textDecoration: "none",
              "&:hover, &:focus-visible": {
                textDecoration: "underline",
                backgroundColor: "transparent",
              },
            },
            "& p>i": {
              color: darkMode ? "#fff !important" : "grey",
            },
            variants: [
              { props: { container: true }, style: { width: "100%" } },
            ],
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            "& a": {
              color: darkMode ? "#71cbf5" : "blue",
              textDecoration: "none",
              "&:hover, &:focus-visible": {
                textDecoration: "underline",
                backgroundColor: "transparent",
              },
            },
          },
        },
      },
      MuiMenuItem: { styleOverrides: { root: { whiteSpace: "normal" } } },
      MuiSelect: { styleOverrides: { select: { whiteSpace: "normal" } } },
      MuiFormControl: {
        styleOverrides: {
          root: {
            "& div:hover:not(.Mui-disabled):before": {
              borderBottomColor: darkMode
                ? "rgba(255, 255, 255, 0.7)"
                : "rgba(0, 0, 0, 0.4)",
            },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: { color: darkMode ? undefined : "rgba(0, 0, 0, 0.8)" },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            color: getTextColor(darkMode),
            "&.Mui-disabled": {
              color: getTextColor(darkMode),
              textFillColor: getTextColor(darkMode),
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            "&": {
              color: darkMode ? undefined : "rgba(0, 0, 0, 0.7)",
            },
            "&.MuiInputLabel-shrink": {
              fontWeight: "bold",
              color: getTextColor(darkMode),
            },
            "&.Mui-disabled": { color: getTextColor(darkMode) },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            "& .MuiSwitch-switchBase.Mui-checked.Mui-disabled": {
              color: darkMode ? "rgb(78 129 81)" : "rgb(113, 164, 116)",
            },
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          label: {
            color: getTextColor(darkMode),
            "&.Mui-disabled": { color: getTextColor(darkMode) },
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          a: {
            color: darkMode ? "#71cbf5" : "blue",
            "&:hover, &:focus-visible": {
              textDecoration: "underline",
              backgroundColor: "transparent",
            },
          },
          "#root a:not(.MuiButtonBase-root)": {
            "&:hover, &:focus-visible": { textDecoration: "underline" },
          },
        },
      },
    },
  });
}

export default getTheme;
